import { Button, Flex, Spin, message } from "antd"
import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { downloadDocument } from "../api/signing.services"
import SigningDocumentView from "./SigningDocumentView"

interface Props {
  session: string
  documentUrl: string | null
  documentBlob: Blob | null
  documentName: string
  recipientName: string
  isSigner: boolean
}

/**
 * The document itself, shown from the blob the page already holds.
 *
 * Saving it asks the server again rather than reusing that blob: a download is
 * its own event, and one taken off a URL the browser already has is one the
 * audit trail never hears about.
 */
const DocumentStep: React.FC<Props> = ({
  session,
  documentUrl,
  documentBlob,
  documentName,
  recipientName,
  isSigner
}) => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)

  const save = useCallback(async () => {
    setSaving(true)
    const response = await downloadDocument(session, documentName)
    setSaving(false)
    if (!response.ok) void message.error(t("signing.document.failed"))
  }, [session, documentName, t])

  if (!documentUrl) {
    return (
      <Flex justify="center" style={{ padding: "48px 0" }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16}>
      <SigningDocumentView
        session={session}
        documentUrl={documentUrl}
        documentBlob={documentBlob}
        recipientName={recipientName}
        isSigner={isSigner}
        height="60vh"
      />

      <Flex gap={8} wrap>
        <Button
          size="large"
          loading={saving}
          onClick={() => void save()}
          style={{ flex: "1 1 200px", minHeight: 44 }}
        >
          {t("signing.document.download")}
        </Button>
      </Flex>
    </Flex>
  )
}

export default DocumentStep
